import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { DataProvider } from "@/context/DataContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import Tabungan from "@/pages/Tabungan";
import Budget from "@/pages/Budget";
import Seserahan from "@/pages/Seserahan";
import Rundown from "@/pages/Rundown";
import Vendor from "@/pages/Vendor";
import Perbandingan from "@/pages/Perbandingan";
import Tamu from "@/pages/Tamu";
import Life from "@/pages/Life";
import Todo from "@/pages/Todo";
import Dokumen from "@/pages/Dokumen";
import Pembayaran from "@/pages/Pembayaran";
import Profile from "@/pages/Profile";
import Undangan from "@/pages/Undangan";
import UsersPage from "@/pages/UsersPage";
import PasanganPage from "@/pages/PasanganPage";
import RegLinks from "@/pages/RegLinks";
import Invitation from "@/pages/Invitation";
import InvitationPremium from "@/pages/InvitationPremium";
import PublicInvitation from "@/pages/PublicInvitation";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import Landing from "@/pages/Landing";
import PaketPage from "@/pages/PaketPage";
import PartnerAccept from "@/pages/PartnerAccept";
import PersetujuanPaket from "@/pages/PersetujuanPaket";
import { HargaPaket, ManajemenPengguna, RiwayatTransaksi, ManajemenTemplate } from "@/pages/AdminManagement";
import TestimonialsAdmin from "@/pages/TestimonialsAdmin";
import ContentAdmin from "@/pages/ContentAdmin";
import FeatureAccessAdmin from "@/pages/FeatureAccessAdmin";
import FeatureGate from "@/components/FeatureGate";
import Moodboard from "@/pages/Moodboard";
import VendorPilihan from "@/pages/VendorPilihan";
import BudgetScenarios from "@/pages/BudgetScenarios";
import { VendorListingAdmin, BudgetScenariosAdmin, AppSettings } from "@/pages/VendorAdminExtras";
import { PaymentSuccess, PaymentFailed, PaymentPending } from "@/pages/PaymentResult";

/**
 * Radix modal pointer-events / scroll-lock cleanup.
 * When a Dialog closes, Radix occasionally leaves `pointer-events: none` on
 * <body> or a leftover `data-scroll-locked` attribute — freezing scroll on
 * desktop. We observe the body and remove those artifacts once no dialog
 * is open, so mouse-wheel scroll always works.
 */
function ScrollLockGuard() {
  React.useEffect(() => {
    const cleanup = () => {
      const dialogOpen =
        document.querySelector('[role="dialog"][data-state="open"]') ||
        document.querySelector('[role="alertdialog"][data-state="open"]');
      if (!dialogOpen) {
        if (document.body.style.pointerEvents === "none") document.body.style.pointerEvents = "";
        if (document.body.hasAttribute("data-scroll-locked")) document.body.removeAttribute("data-scroll-locked");
        // Radix RemoveScroll sometimes leaves overflow:hidden on <html>/<body>
        if (document.body.style.overflow === "hidden") document.body.style.overflow = "";
      }
    };
    const observer = new MutationObserver(cleanup);
    observer.observe(document.body, { attributes: true, attributeFilter: ["style", "data-scroll-locked"] });
    observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state"] });
    return () => observer.disconnect();
  }, []);
  return null;
}

function Splash() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a0705] text-white">
      <div className="flex flex-col items-center gap-3">
        <img src="/wedifylab_logo3.png" alt="Wedifylab" className="h-14 w-14 object-contain drop-shadow-[0_0_20px_#c9a35c66]" />
        <div className="font-heading text-3xl font-light tracking-tight text-[#e6c98a]">Wedifylab…</div>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/" replace />;
  // Gate: non-admin users with inactive plan → /paket
  if (user.role !== "sysadmin" && user.role !== "Admin" && user.plan_status !== "active") {
    return <Navigate to="/paket" replace />;
  }
  return children;
}

function PaketGuard({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role === "sysadmin") return children;
  return <Navigate to="/app" replace />;
}

function RegLinksAccess({ children }) {
  const { user } = useAuth();
  if (user && (user.role === "sysadmin" || user.role === "Admin")) return children;
  return <Navigate to="/app" replace />;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user) return <Navigate to="/app" replace />;
  return <Login />;
}

function LandingRoute() {
  const { user } = useAuth();
  if (user) return <Navigate to="/app" replace />;
  return <Landing />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <DataProvider>
            <ScrollLockGuard />
            <Toaster position="top-center" richColors />
            <Routes>
              <Route path="/" element={<LandingRoute />} />
              <Route path="/login" element={<LoginRoute />} />
              <Route path="/u/:pid" element={<PublicInvitation />} />
              <Route path="/undangan/:pid" element={<Invitation />} />
              <Route path="/undangan-premium/:pid" element={<InvitationPremium />} />
              <Route path="/daftar" element={<Register />} />
              <Route path="/daftar/:token" element={<Register />} />
              <Route path="/undang-pasangan/:token" element={<PartnerAccept />} />
              <Route path="/paket" element={<PaketGuard><PaketPage /></PaketGuard>} />
              <Route path="/payment/success" element={<PaketGuard><PaymentSuccess /></PaketGuard>} />
              <Route path="/payment/failed" element={<PaketGuard><PaymentFailed /></PaketGuard>} />
              <Route path="/payment/pending" element={<PaketGuard><PaymentPending /></PaketGuard>} />
              <Route path="/lupa-password" element={<ResetPassword />} />
              <Route path="/app" element={<Protected><Layout /></Protected>}>
                <Route index element={<Home />} />
                <Route path="tabungan" element={<FeatureGate featureId="tabungan" label="Tabungan"><Tabungan /></FeatureGate>} />
                <Route path="budget" element={<FeatureGate featureId="budget" label="Budget"><Budget /></FeatureGate>} />
                <Route path="seserahan" element={<FeatureGate featureId="seserahan" label="List Seserahan"><Seserahan /></FeatureGate>} />
                <Route path="rundown" element={<FeatureGate featureId="rundown" label="Rundown Acara"><Rundown /></FeatureGate>} />
                <Route path="vendor" element={<FeatureGate featureId="vendor" label="Kontak Vendor"><Vendor /></FeatureGate>} />
                <Route path="perbandingan" element={<FeatureGate featureId="perbandingan" label="Perbandingan Vendor"><Perbandingan /></FeatureGate>} />
                <Route path="tamu" element={<FeatureGate featureId="tamu" label="List Tamu"><Tamu /></FeatureGate>} />
                <Route path="life" element={<FeatureGate featureId="life" label="Life After Married"><Life /></FeatureGate>} />
                <Route path="todo" element={<FeatureGate featureId="todo" label="To Do List"><Todo /></FeatureGate>} />
                <Route path="dokumen" element={<FeatureGate featureId="dokumen" label="List Dokumen"><Dokumen /></FeatureGate>} />
                <Route path="pembayaran" element={<FeatureGate featureId="pembayaran" label="Pembayaran"><Pembayaran /></FeatureGate>} />
                <Route path="profile" element={<Profile />} />
                <Route path="undangan" element={<FeatureGate featureId="undangan" label="Kustomisasi Undangan"><Undangan /></FeatureGate>} />
                <Route path="users" element={<AdminOnly><UsersPage /></AdminOnly>} />
                <Route path="reg-links" element={<RegLinksAccess><RegLinks /></RegLinksAccess>} />
                <Route path="persetujuan-paket" element={<RegLinksAccess><PersetujuanPaket /></RegLinksAccess>} />
                <Route path="harga-paket" element={<RegLinksAccess><HargaPaket /></RegLinksAccess>} />
                <Route path="manajemen-pengguna" element={<RegLinksAccess><ManajemenPengguna /></RegLinksAccess>} />
                <Route path="transaksi" element={<RegLinksAccess><RiwayatTransaksi /></RegLinksAccess>} />
                <Route path="template-manager" element={<RegLinksAccess><ManajemenTemplate /></RegLinksAccess>} />
                <Route path="testimonials" element={<RegLinksAccess><TestimonialsAdmin /></RegLinksAccess>} />
                <Route path="konten-halaman" element={<RegLinksAccess><ContentAdmin /></RegLinksAccess>} />
                <Route path="akses-fitur" element={<RegLinksAccess><FeatureAccessAdmin /></RegLinksAccess>} />
                <Route path="moodboard" element={<FeatureGate featureId="moodboard" label="Moodboard"><Moodboard /></FeatureGate>} />
                <Route path="skenario-budget" element={<FeatureGate featureId="skenario_budget" label="Skenario Budget"><BudgetScenarios /></FeatureGate>} />
                <Route path="vendor-pilihan" element={<FeatureGate featureId="vendor_pilihan" label="Vendor Pilihan"><VendorPilihan /></FeatureGate>} />
                <Route path="vendor-listing" element={<RegLinksAccess><VendorListingAdmin /></RegLinksAccess>} />
                <Route path="skenario-budget-admin" element={<RegLinksAccess><BudgetScenariosAdmin /></RegLinksAccess>} />
                <Route path="settings-app" element={<RegLinksAccess><AppSettings /></RegLinksAccess>}/>
                <Route path="pasangan" element={<AdminOnly><PasanganPage /></AdminOnly>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </DataProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
